import React from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const RegisteredEvents = ({ events, onViewEvent, onCancelRegistration, onAccessMaterials }) => {
  const upcomingEvents = events?.filter(event => new Date(event.dateTime) > new Date());
  const pastEvents = events?.filter(event => new Date(event.dateTime) <= new Date());

  const formatEventDate = (dateTime) => {
    const date = new Date(dateTime);
    return {
      date: date?.toLocaleDateString('en-US', { 
        month: 'short', 
        day: 'numeric' 
      }),
      time: date?.toLocaleTimeString('en-US', { 
        hour: 'numeric', 
        minute: '2-digit', 
        hour12: true 
      })
    };
  };

  const EventItem = ({ event, isPast = false }) => {
    const { date, time } = formatEventDate(event?.dateTime);
    
    return (
      <div className="flex items-center space-x-4 p-4 bg-card border border-border rounded-lg hover:shadow-wellness transition-wellness">
        {/* Event Image */}
        <div className="w-16 h-16 rounded-lg overflow-hidden flex-shrink-0">
          <Image
            src={event?.image}
            alt={event?.title}
            className="w-full h-full object-cover"
          />
        </div>
        {/* Event Info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between">
            <div className="flex-1 min-w-0">
              <h4 className="font-heading font-medium text-foreground truncate">
                {event?.title}
              </h4>
              <p className="text-sm text-muted-foreground mt-1">
                {event?.expert?.name} • {event?.expert?.credentials}
              </p>
              <div className="flex items-center space-x-4 mt-2">
                <div className="flex items-center space-x-1">
                  <Icon name="Calendar" size={14} className="text-muted-foreground" />
                  <span className="text-xs text-muted-foreground">{date}</span>
                </div>
                <div className="flex items-center space-x-1">
                  <Icon name="Clock" size={14} className="text-muted-foreground" />
                  <span className="text-xs text-muted-foreground">{time}</span>
                </div>
                <div className="flex items-center space-x-1">
                  <Icon 
                    name={event?.type === 'Virtual' ? 'Video' : 'MapPin'} 
                    size={14} 
                    className="text-muted-foreground" 
                  />
                  <span className="text-xs text-muted-foreground">{event?.type}</span>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex items-center space-x-2 ml-4">
              {isPast ? (
                <>
                  {event?.hasMaterials && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => onAccessMaterials(event)}
                      iconName="FileText"
                      iconPosition="left"
                    >
                      Materials
                    </Button>
                  )}
                  {event?.hasRecording && (
                    <Button
                      variant="default"
                      size="sm"
                      onClick={() => onViewEvent(event)}
                      iconName="Play"
                      iconPosition="left"
                    >
                      Watch
                    </Button>
                  )}
                </>
              ) : (
                <>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => onViewEvent(event)}
                  >
                    View Details
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => onCancelRegistration(event)}
                    iconName="X"
                    iconPosition="left"
                  >
                    Cancel
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  if (events?.length === 0) {
    return (
      <div className="bg-card border border-border rounded-lg p-8 text-center">
        <Icon name="Calendar" size={48} className="mx-auto text-muted-foreground mb-4" />
        <h3 className="font-heading font-medium text-foreground mb-2">
          No Registered Events
        </h3>
        <p className="text-muted-foreground mb-4">
          You haven't registered for any events yet. Explore our upcoming workshops and events.
        </p>
        <Button variant="default" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
          Browse Events
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Upcoming Events */}
      {upcomingEvents?.length > 0 && (
        <div>
          <div className="flex items-center space-x-2 mb-4">
            <Icon name="Calendar" size={20} className="text-primary" />
            <h3 className="font-heading font-semibold text-foreground">
              Upcoming Events ({upcomingEvents?.length})
            </h3>
          </div>
          <div className="space-y-3">
            {upcomingEvents?.map((event) => (
              <EventItem key={event?.id} event={event} />
            ))}
          </div>
        </div>
      )}
      {/* Past Events */}
      {pastEvents?.length > 0 && (
        <div>
          <div className="flex items-center space-x-2 mb-4">
            <Icon name="History" size={20} className="text-muted-foreground" />
            <h3 className="font-heading font-semibold text-foreground">
              Past Events ({pastEvents?.length})
            </h3>
          </div>
          <div className="space-y-3">
            {pastEvents?.map((event) => (
              <EventItem key={event?.id} event={event} isPast={true} />
            ))}
          </div>
        </div>
      )}
      {/* Quick Actions */}
      <div className="bg-muted/30 border border-border rounded-lg p-4">
        <h4 className="font-heading font-medium text-foreground mb-3">Quick Actions</h4>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Button
            variant="outline"
            onClick={() => window.location.href = '/progress-tracking'}
            iconName="TrendingUp"
            iconPosition="left"
            className="justify-start"
          >
            View Progress
          </Button>
          <Button
            variant="outline"
            onClick={() => window.location.href = '/community-forums'}
            iconName="MessageSquare"
            iconPosition="left"
            className="justify-start"
          >
            Share Experience
          </Button>
        </div>
      </div>
    </div>
  );
};

export default RegisteredEvents;